import type { ChecklistItem, Task } from '@nedelnik/shared';

/**
 * Поиск и фильтры (3.12, 3.10.3).
 *
 * Фильтр — одно состояние на весь экран, а не набор независимых переключателей:
 * человек должен видеть **весь** список условий, по которым сокращён список
 * (3.12.4). Два фильтра, каждый из которых знает только про себя, показывают
 * половину правды.
 */

export interface Filter {
  /** Тег, по которому отфильтровано (3.10.3). */
  tag: string | undefined;
  /** Строка поиска; пустая — не фильтрует. */
  query: string;
  /**
   * Искать во всех спринтах, а не только в показанном (3.12.3). Умолчание —
   * показанный спринт: поиск нужен изредка и обычно про то, что перед глазами.
   */
  allSprints: boolean;
}

export const EMPTY_FILTER: Filter = { tag: undefined, query: '', allSprints: false };

/** Сокращает ли фильтр список. Ровно этот ответ и показывается человеку. */
export function isFiltering(filter: Filter): boolean {
  return filter.tag !== undefined || filter.query.trim().length > 0;
}

function contains(haystack: string | null | undefined, needle: string): boolean {
  return typeof haystack === 'string' && haystack.toLocaleLowerCase().includes(needle);
}

/**
 * Совпадение по заголовку, описанию и пунктам чек-листа (3.12.2).
 *
 * Пункты входят намеренно: половина деталей задачи живёт именно в них, и поиск,
 * который их не видит, отвечает «ничего не найдено» на существующую запись.
 */
export function matchesQuery(
  task: Task,
  query: string,
  items: readonly ChecklistItem[] = [],
): boolean {
  const needle = query.trim().toLocaleLowerCase();
  if (needle.length === 0) return true;

  if (contains(task.title, needle) || contains(task.notes, needle)) return true;
  return items.some((item) => contains(item.text, needle));
}

export interface FilterContext {
  tags: ReadonlyMap<string, string[]>;
  checklist: ReadonlyMap<string, ChecklistItem[]>;
}

/**
 * Применение фильтра к уже выбранному набору задач.
 *
 * Периметр (показанный спринт или все задачи) выбирает вызывающий: правило
 * показа задачи в периоде живёт в `shared` и не должно повторяться здесь.
 */
export function applyFilter(
  tasks: readonly Task[],
  filter: Filter,
  context: FilterContext,
): Task[] {
  if (!isFiltering(filter)) return [...tasks];

  return tasks.filter((task) => {
    if (filter.tag !== undefined && !(context.tags.get(task.id) ?? []).includes(filter.tag)) {
      return false;
    }
    return matchesQuery(task, filter.query, context.checklist.get(task.id) ?? []);
  });
}

/**
 * Подбор задачи по названию — для связей (3.9.2) и для выбора проекта.
 *
 * Ищет только по заголовку: в подборщике человек вспоминает название, а
 * совпадение по описанию давало бы строки, в которых искомых слов не видно.
 */
export function findByTitle(tasks: readonly Task[], query: string, limit = 8): Task[] {
  const needle = query.trim().toLocaleLowerCase();
  const matched =
    needle.length === 0 ? [...tasks] : tasks.filter((task) => contains(task.title, needle));

  return matched
    .sort((a, b) => {
      // Незакрытые выше: связывают и вкладывают обычно то, что ещё предстоит.
      const openness = Number(a.status === 'done') - Number(b.status === 'done');
      if (openness !== 0) return openness;
      return (a.title ?? '').localeCompare(b.title ?? '');
    })
    .slice(0, limit);
}
